import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import {
    EnvelopeIcon,
    PhoneIcon,
    StarIcon,
    GlobeAltIcon,
    FlagIcon,
    XMarkIcon,
    PlusCircleIcon,
    CheckCircleIcon,
    UserGroupIcon,
} from "@heroicons/react/24/solid";
import { FlagIcon as FlagIconSolid } from "@heroicons/react/24/solid";
import { FaFacebook, FaInstagram, FaTwitter, FaTiktok, FaYoutube, FaLinkedinIn, FaUber } from "react-icons/fa";
import { Bookmark } from "lucide-react";
import { getFounderEstablishmentCounts, getEstablishmentReportCounts, reportEstablishment, removeEstablishmentReport, checkEstablishmentOwner, CheckOwnerResult } from "../../interfaces/establishments";
import { createProspect } from "../../interfaces/prospect";
import { useNavigate } from "react-router-dom";
import { EstablishmentLogo } from "../franchise/EstablishmentLogo";
import { EstablishmentReportCounts, EstablishmentReportType } from "../../interfaces/establishment";
import { REPORT_LABELS, REPORT_TEXT_COLORS as REPORT_COLORS, PAYMENT_REPORT_TYPES } from "../../utils/reportConstants";
import ReportSignalBadge from "./ReportSignalBadge";
import { AddToListPicker } from "../prospection/AddToListPicker";
import { getUpcomingAnniversary, formatAnniversaryDelay } from "../../lib/anniversary";
import { countMatchesNbEtab } from "../../lib/nbEtabBuckets";
import { FounderCounts, founderCountLabel } from "../../lib/founderCounts";

interface SignalTableProps {
    signals: any[];
    onSaveToggle: (id: string, saved: boolean) => void;
    dateLabel?: string;
    activeSignalTypes?: string[];
    showAnniversary?: boolean; // show upcoming opening anniversary in the date column
    phoneTypeFilter?: ('mobile' | 'landline')[];
    emailTypeFilter?: ('owner' | 'business')[];
    /** When set (founder page), show the person whose name was searched for instead of
     *  peoples[0] — an establishment matches on ANY of its dirigeants, so the first one
     *  in the array is often someone else entirely. */
    focusPersonName?: string;
    /** Active "Nb établissements" buckets. Same reason as focusPersonName: the backend
     *  matches a row when ANY of its dirigeants falls in the selected range, so the row must
     *  show that dirigeant rather than peoples[0], who may run a completely different number
     *  of establishments. Empty = no constraint, peoples[0] as before. */
    nbEtabBuckets?: string[];
    /** Fermetures folder: count and display closures rather than what is still open. */
    countClosed?: boolean;
    dateSortOrder?: 'asc' | 'desc' | null;
    onDateSortClick?: () => void;
    onOwnersEnriched?: () => void;
}

function isMobilePhone(phone: string): boolean {
    const digits = phone.replace(/\s/g, '');
    return /^(06|07|\+336|\+337|\+3306|\+3307)/.test(digits);
}

function formatPhoneNumber(phone: string): string {
    const digits = phone.replace(/[\s\-().]/g, '');
    if (digits.startsWith('+33')) {
        let national = digits.slice(3);
        if (national.startsWith('0')) national = national.slice(1);
        if (national.length === 9)
            return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
    }
    if (digits.startsWith('0') && digits.length === 10)
        return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    return phone;
}

// Smart calling time recommendation system for French restaurants
interface CallingWindowInfo {
    status: 'excellent' | 'good' | 'acceptable' | 'avoid' | 'closed';
    message: string;
    nextWindow: {
        timeRange: string;
        startsIn: string;
        isToday: boolean;
    } | null;
    currentColor: string;
    currentIcon: string;
}

function getRestaurantCallingStatus(): CallingWindowInfo {
    const now = new Date();
    const parisTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    const hour = parisTime.getHours();
    const minute = parisTime.getMinutes();
    const currentMinutes = hour * 60 + minute;

    // Define time windows (in minutes from midnight)
    const windows = {
        excellentMorning: { start: 10 * 60, end: 11 * 60 + 30 }, // 10h00-11h30
        excellentAfternoon: { start: 15 * 60, end: 17 * 60 + 30 }, // 15h00-17h30
        goodMorning: { start: 9 * 60 + 30, end: 10 * 60 }, // 9h30-10h00
        goodEvening: { start: 17 * 60 + 30, end: 18 * 60 + 30 }, // 17h30-18h30
        lunchService: { start: 11 * 60 + 30, end: 15 * 60 }, // 11h30-15h00
        dinnerService: { start: 18 * 60 + 30, end: 22 * 60 + 30 }, // 18h30-22h30
        closed: { start: 22 * 60 + 30, end: 9 * 60 + 30 }, // 22h30-9h30
    };

    const formatTime = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
    };

    const getNextWindow = (fromMinutes: number) => {
        const tomorrow = new Date(parisTime);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        let nextStart: number;
        let nextEnd: number;
        let isToday = true;

        if (fromMinutes < windows.goodMorning.start) {
            nextStart = windows.goodMorning.start;
            nextEnd = windows.excellentMorning.end;
        } else if (fromMinutes < windows.excellentMorning.end) {
            nextStart = windows.excellentAfternoon.start;
            nextEnd = windows.excellentAfternoon.end;
        } else if (fromMinutes < windows.excellentAfternoon.end) {
            nextStart = windows.goodEvening.start;
            nextEnd = windows.goodEvening.end;
        } else {
            // Next day morning
            nextStart = windows.goodMorning.start;
            nextEnd = windows.excellentMorning.end;
            isToday = false;
        }

        const minsUntil = isToday ? nextStart - fromMinutes : (24 * 60 - fromMinutes) + nextStart;
        const hours = Math.floor(minsUntil / 60);
        const mins = minsUntil % 60;
        
        let startsIn = '';
        if (hours > 0 && mins > 0) {
            startsIn = `dans ${hours}h${mins.toString().padStart(2, '0')}`;
        } else if (hours > 0) {
            startsIn = `dans ${hours}h`;
        } else if (mins > 15) {
            startsIn = `dans ${mins} min`;
        } else if (mins > 0) {
            startsIn = `dans ${mins} min`;
        }

        return {
            timeRange: `${formatTime(nextStart)} - ${formatTime(nextEnd)}`,
            startsIn,
            isToday,
        };
    };

    // Excellent windows
    if (currentMinutes >= windows.excellentMorning.start && currentMinutes < windows.excellentMorning.end) {
        return {
            status: 'excellent',
            message: 'Moment idéal pour appeler',
            nextWindow: null,
            currentColor: '#10b981',
            currentIcon: ''
        };
    }

    if (currentMinutes >= windows.excellentAfternoon.start && currentMinutes < windows.excellentAfternoon.end) {
        return {
            status: 'excellent',
            message: 'Moment idéal pour appeler',
            nextWindow: null,
            currentColor: '#10b981',
            currentIcon: ''
        };
    }

    // Good windows
    if (currentMinutes >= windows.goodMorning.start && currentMinutes < windows.excellentMorning.start) {
        return {
            status: 'good',
            message: 'Bon moment pour appeler',
            nextWindow: null,
            currentColor: '#22c55e',
            currentIcon: ''
        };
    }

    if (currentMinutes >= windows.goodEvening.start && currentMinutes < windows.dinnerService.start) {
        return {
            status: 'good',
            message: 'Bon moment pour appeler',
            nextWindow: null,
            currentColor: '#22c55e',
            currentIcon: ''
        };
    }

    // Service hours - avoid
    if (currentMinutes >= windows.lunchService.start && currentMinutes < windows.lunchService.end) {
        return {
            status: 'avoid',
            message: 'Service déjeuner en cours',
            nextWindow: getNextWindow(currentMinutes),
            currentColor: '#f59e0b',
            currentIcon: ''
        };
    }

    if (currentMinutes >= windows.dinnerService.start && currentMinutes < windows.dinnerService.end) {
        return {
            status: 'avoid',
            message: 'Service dîner en cours',
            nextWindow: getNextWindow(currentMinutes),
            currentColor: '#f59e0b',
            currentIcon: ''
        };
    }

    // Closed hours
    if (currentMinutes >= windows.closed.start || currentMinutes < windows.closed.end) {
        return {
            status: 'closed',
            message: 'Restaurant fermé',
            nextWindow: getNextWindow(currentMinutes),
            currentColor: '#ef4444',
            currentIcon: ''
        };
    }

    // Default acceptable
    return {
        status: 'acceptable',
        message: 'Vous pouvez appeler',
        nextWindow: getNextWindow(currentMinutes),
        currentColor: '#3b82f6',
        currentIcon: ''
    };
}

// Signal-type pills shown under the contact name. These three are deliberately not rendered:
// the opening pill only repeats what the folder and the "Ouverture" date column already say,
// multi-site repeats the "N établissements" line directly above it, and BODACC is an internal
// source tag with no user-facing label, so it leaked through as the raw type string.
const HIDDEN_SIGNAL_BADGES = new Set(["restaurant_opening", "multi_site", "bodacc_announcement"]);

const SIGNAL_TYPE_ICON: Record<string, string> = {
    'restaurant_opening': '🏪',
    'restaurant_owner_change': '👤',
    'multi_site': '📍',
    'restaurant_closing': '🔒',
    'hygiene_control': '🏥',
    'franchise_signing': '🤝',
    'network_expansion': '🌐',
};
const SIGNAL_TYPE_LABEL: Record<string, string> = {
    'restaurant_opening': 'Ouverture',
    'restaurant_owner_change': 'Changement gérant',
    'multi_site': 'Multi-site',
    'restaurant_closing': 'Fermeture',
    'hygiene_control': 'Contrôle sanitaire',
    'franchise_signing': 'Franchise',
    'network_expansion': 'Expansion',
};

// Helper: extract enrichment data from a person's enrichments array
function getEnrichment(enrichments: any[], type: string) {
    return enrichments?.find(
        (e: any) => e.type === type && (e.status === "done" || e.status === "approved")
    );
}

// Capitalize first letter of each word in a city name
function formatCity(city: string): string {
    return city.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Format name: first letter uppercase, rest lowercase, for each word
function formatName(name: string): string {
    return name.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Compute age from "MM-YYYY" birthdate string
function computeAgeFromBirthdate(birthdateStr: string): number | null {
    const parts = birthdateStr.split("-");
    if (parts.length !== 2) return null;
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    if (isNaN(month) || isNaN(year)) return null;
    const now = new Date();
    let age = now.getFullYear() - year;
    if (now.getMonth() + 1 < month) age--;
    return age;
}

function displayEstablishmentName(name: string | undefined): string {
    if (!name) return "";
    return name.includes("[ND]") ? "Établissement confidentiel" : name;
}

export default function SignalTable({ signals: rawEstablishments, onSaveToggle, dateLabel = "Ouverture", activeSignalTypes, showAnniversary, phoneTypeFilter, emailTypeFilter, focusPersonName, nbEtabBuckets, countClosed, dateSortOrder, onDateSortClick, onOwnersEnriched }: SignalTableProps) {
    const navigate = useNavigate();
    const isSub = localStorage.getItem("isSub") === "true";
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    const trialEndsAt = localStorage.getItem("trialEndsAt");
    const isTrialUser = !isSub && !isAdmin && !!trialEndsAt;
    // Establishment count per person _id, for every dirigeant on the page (not just the
    // first): pickPerson needs them to tell which one satisfied the nb-établissements filter.
    const [personNameCounts, setPersonNameCounts] = useState<Record<string, FounderCounts>>({});

    // The row's contact must be the person the list was filtered on; falling back to
    // peoples[0] is what made a "THI NGUYEN" search display "Thi Le" or "Tran Nguyen".
    const pickPerson = (est: any) => {
        const people = est?.peoples || [];
        if (focusPersonName) {
            const wanted = focusPersonName.trim().toLowerCase();
            const match = people.find((p: any) => (p?.name || "").trim().toLowerCase() === wanted);
            if (match) return match;
        }
        if (nbEtabBuckets && nbEtabBuckets.length > 0) {
            const match = people.find((p: any) =>
                countMatchesNbEtab(
                    countClosed ? personNameCounts[p?._id]?.closed : personNameCounts[p?._id]?.active,
                    nbEtabBuckets
                )
            );
            if (match) return match;
        }
        return people[0];
    };
    // Memoised because this is the sole dependency of the founder-counts effect below, which
    // sets state. Without it, a non-empty phoneTypeFilter produces a fresh array identity on
    // every render: effect fires -> counts land -> re-render -> new identity -> effect fires
    // again, a request loop for as long as the filter is on.
    const establishments = useMemo(
        () => (phoneTypeFilter && phoneTypeFilter.length > 0
            ? rawEstablishments.filter(est => {
                const phone: string | undefined = est.phone || est.peoples?.[0]?.enrichments?.find((e: any) => e.type === 'phone' && (e.status === 'done' || e.status === 'approved'))?.data?.phone;
                if (!phone) return false;
                const isMobile = isMobilePhone(phone);
                return phoneTypeFilter.some(type => type === 'mobile' ? isMobile : !isMobile);
            })
            : rawEstablishments),
        [rawEstablishments, phoneTypeFilter]
    );
    const visibleCount = isTrialUser ? Math.ceil(establishments.length / 3) : establishments.length;
    const [selectedSignals, setSelectedSignals] = useState<Set<string>>(new Set());
    const [hoveredLinkedin, setHoveredLinkedin] = useState<string | null>(null);
    const [hoveredPhone, setHoveredPhone] = useState<string | null>(null);
    const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
    const [callingStatus, setCallingStatus] = useState<CallingWindowInfo>(getRestaurantCallingStatus());

    // Admin bulk "enrich owners" action
    const [enrichingOwners, setEnrichingOwners] = useState(false);
    const [enrichProgress, setEnrichProgress] = useState<{ done: number; total: number } | null>(null);
    const [ownerEnrichResults, setOwnerEnrichResults] = useState<Array<{ id: string; name: string; result?: CheckOwnerResult; error?: string }> | null>(null);

    const phonePopupRef = useRef<HTMLDivElement>(null);
    const linkedinPopupRef = useRef<HTMLDivElement>(null);
    const emailPopupRef = useRef<HTMLDivElement>(null);

    // Close popups on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (phonePopupRef.current && !phonePopupRef.current.contains(event.target as Node)) setHoveredPhone(null);
            if (linkedinPopupRef.current && !linkedinPopupRef.current.contains(event.target as Node)) setHoveredLinkedin(null);
            if (emailPopupRef.current && !emailPopupRef.current.contains(event.target as Node)) setHoveredEmail(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Update calling status every minute for real-time recommendations
    useEffect(() => {
        const updateCallingStatus = () => setCallingStatus(getRestaurantCallingStatus());
        updateCallingStatus(); // Initial update
        
        const interval = setInterval(updateCallingStatus, 60000); // Update every minute
        return () => clearInterval(interval);
    }, []);

    const handleManualSearch = (e: React.MouseEvent, name: string) => {
        e.stopPropagation();
        window.open(`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(name)}`, "_blank");
    };

    const handleOpenLinkedin = (e: React.MouseEvent, url: string) => {
        e.stopPropagation();
        window.open(url, "_blank");
    };

    const handleCopyPhone = async (e: React.MouseEvent, phone: string) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(phone); } catch { /* ignore */ }
    };

    const handleCopyEmail = async (e: React.MouseEvent, email: string) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(email); } catch { /* ignore */ }
    };

    const toggleSelectAll = () => {
        if (selectedSignals.size === establishments.length) {
            setSelectedSignals(new Set());
        } else {
            setSelectedSignals(new Set(establishments.map(e => e._id)));
        }
    };

    // reportType per establishmentId, null = no report
    const [myReports, setMyReports] = useState<Record<string, EstablishmentReportType | null>>({});
    // What everybody else reported, per establishmentId — drives the warning badge on the row.
    const [reportCounts, setReportCounts] = useState<Record<string, EstablishmentReportCounts>>({});
    const [reportOpen, setReportOpen] = useState<string | null>(null);
    const [reportComment, setReportComment] = useState("");
    const [reportLoading, setReportLoading] = useState<string | null>(null);
    const reportPopupRef = useRef<HTMLDivElement>(null);

    // Add-to-LightCRM state
    const [crmLoading, setCrmLoading] = useState<string | null>(null);
    const [lightCrmSuccess, setLightCrmSuccess] = useState<Record<string, boolean>>({});
    const [crmPopupOpen, setCrmPopupOpen] = useState<string | null>(null);
    const [crmPopupPos, setCrmPopupPos] = useState<{ top: number; left: number } | null>(null);
    const crmPopupRef = useRef<HTMLDivElement>(null);

    // Close report popup on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (reportPopupRef.current && !reportPopupRef.current.contains(e.target as Node)) {
                setReportOpen(null);
                setReportComment("");
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    // Close CRM popup on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (crmPopupRef.current && !crmPopupRef.current.contains(e.target as Node)) {
                setCrmPopupOpen(null);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const handleAddToLightCrm = async (est: any) => {
        const primaryPerson = pickPerson(est);
        const enrichments: any[] = primaryPerson?.enrichments || [];
        const ownerName: string | undefined = primaryPerson?.name;
        const phoneEnrichment = getEnrichment(enrichments, "phone");
        const emailCompanyEnrichment = getEnrichment(enrichments, "email_company");
        const emailPersonalEnrichment = getEnrichment(enrichments, "email");
        const linkedinEnrichment = getEnrichment(enrichments, "linkedin");
        const estPhone: string | undefined = est.phone || phoneEnrichment?.data?.phone;
        const businessEmail: string | undefined = est.email || emailCompanyEnrichment?.data?.email;
        const ownerEmail: string | undefined = emailPersonalEnrichment?.data?.email;
        const linkedin: string | undefined = linkedinEnrichment?.data?.url;

        const socialNetworks: Record<string, string> = {};
        est.social_networks?.forEach((n: any) => {
            if (n.url && n.platform) socialNetworks[n.platform] = n.url;
        });

        const location = est.address?.city
            ? `${formatCity(est.address.city)}${est.address?.postcode ? ` (${est.address.postcode})` : ""}`
            : undefined;

        const fullName = ownerName ? formatName(ownerName) : displayEstablishmentName(est.name);
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        setCrmLoading(`${est._id}-lightcrm`);
        try {
            await createProspect({
                name: fullName,
                firstName,
                lastName,
                company: displayEstablishmentName(est.name),
                email: businessEmail || ownerEmail,
                phone: estPhone,
                website: est.website_url,
                linkedinUrl: linkedin,
                location,
                socialNetworks: Object.keys(socialNetworks).length > 0 ? socialNetworks : undefined,
                establishmentId: est._id,
                source: "signal",
                status: "new",
            });
            setLightCrmSuccess(prev => ({ ...prev, [est._id]: true }));
            setCrmPopupOpen(null);
        } catch { /* ignore */ }
        setCrmLoading(null);
    };

    // Re-read one row's counts from the server rather than adjusting them locally: the user
    // may already have had a report on this establishment from an earlier session (myReports
    // starts empty on mount), and a local +1 would then count them twice.
    const refreshReportCounts = async (estId: string) => {
        try {
            const data = await getEstablishmentReportCounts([estId]);
            setReportCounts(prev => ({ ...prev, [estId]: data[estId] ?? { total: 0, byType: {} } }));
        } catch { /* ignore */ }
    };

    const handleReport = async (estId: string, type: EstablishmentReportType) => {
        setReportLoading(estId);
        try {
            await reportEstablishment(estId, type, reportComment || undefined);
            setMyReports(prev => ({ ...prev, [estId]: type }));
            setReportOpen(null);
            setReportComment("");
            await refreshReportCounts(estId);
        } catch { /* ignore */ }
        setReportLoading(null);
    };

    const handleRemoveReport = async (estId: string) => {
        setReportLoading(estId);
        try {
            await removeEstablishmentReport(estId);
            setMyReports(prev => ({ ...prev, [estId]: null }));
            setReportOpen(null);
            await refreshReportCounts(estId);
        } catch { /* ignore */ }
        setReportLoading(null);
    };

    useEffect(() => {
        const uniqueIds = [...new Set(
            establishments.flatMap(est => (est.peoples ?? []).map((p: any) => p?._id)).filter(Boolean) as string[]
        )];
        if (uniqueIds.length === 0) return;
        getFounderEstablishmentCounts(uniqueIds)
            .then(setPersonNameCounts)
            .catch(() => { /* ignore */ });
    }, [establishments]);

    useEffect(() => {
        const ids = establishments.map(est => est._id).filter(Boolean);
        if (ids.length === 0) return;
        getEstablishmentReportCounts(ids)
            .then(setReportCounts)
            .catch(() => { /* ignore */ });
    }, [establishments]);

    const toggleSelectSignal = (id: string) => {
        setSelectedSignals(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Admin-only: run the owner/dirigeant check (INSEE+BODACC+RNE) for every selected
    // establishment, one at a time so we don't burst the RNE accounts. Same endpoint/logic
    // as the BO "Founder Enrichment" page, just triggered inline without leaving this page.
    const handleEnrichOwners = async () => {
        const ids = Array.from(selectedSignals);
        if (ids.length === 0) return;

        setEnrichingOwners(true);
        setEnrichProgress({ done: 0, total: ids.length });
        const results: Array<{ id: string; name: string; result?: CheckOwnerResult; error?: string }> = [];

        for (const id of ids) {
            const name = displayEstablishmentName(establishments.find(e => e._id === id)?.name) || id;
            try {
                const result = await checkEstablishmentOwner(id);
                results.push({ id, name, result });
            } catch (err: any) {
                results.push({ id, name, error: err?.response?.data?.error || err?.response?.data?.message || "Erreur inconnue" });
            }
            setEnrichProgress(prev => prev ? { ...prev, done: prev.done + 1 } : prev);
        }

        setOwnerEnrichResults(results);
        setEnrichingOwners(false);
        setEnrichProgress(null);
        setSelectedSignals(new Set());
        onOwnersEnriched?.();
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString("fr-FR", { month: "short", day: "numeric", year: "numeric" });
    };

    const getFormattedDate = (dateString?: string) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString("fr-FR", { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const detectSocialMedia = (url: string) => {
        if (!url) return null;
        const l = url.toLowerCase();
        if (l.includes("facebook.com") || l.includes("fb.com")) return { type: "facebook", color: "#1877f2" };
        if (l.includes("instagram.com")) return { type: "instagram", color: "#E4405F" };
        if (l.includes("twitter.com") || l.includes("x.com")) return { type: "twitter", color: "#1DA1F2" };
        if (l.includes("tiktok.com")) return { type: "tiktok", color: "#000000" };
        if (l.includes("youtube.com")) return { type: "youtube", color: "#FF0000" };
        if (l.includes("ubereats.com")) return { type: "ubereats", color: "#06C167" };
        return { type: "website", color: "#6B7280" };
    };

    const getSocialMediaIcon = (type: string) => {
        const p = { className: "w-5 h-5" };
        switch (type) {
            case "facebook": return <FaFacebook {...p} />;
            case "instagram": return <FaInstagram {...p} />;
            case "twitter": return <FaTwitter {...p} />;
            case "tiktok": return <FaTiktok {...p} />;
            case "youtube": return <FaYoutube {...p} />;
            case "linkedin": return <FaLinkedinIn {...p} />;
            case "ubereats": return <FaUber {...p} />;
            default: return <GlobeAltIcon {...p} />;
        }
    };

    return (
        <>
        {selectedSignals.size > 0 && (
            <div className="mb-3 flex items-center justify-between bg-ink text-ink-inverse rounded-lg px-4 py-2.5">
                <span className="text-xs font-medium">
                    {selectedSignals.size} établissement{selectedSignals.size > 1 ? "s" : ""} sélectionné{selectedSignals.size > 1 ? "s" : ""}
                </span>
                <div className="flex items-center gap-2">
                    <AddToListPicker
                        establishmentIds={Array.from(selectedSignals)}
                        onAdded={() => setSelectedSignals(new Set())}
                    >
                        {({ onClick }) => (
                            <button
                                onClick={onClick}
                                className="flex items-center gap-2 px-3 py-1.5 bg-surface text-ink text-xs font-semibold rounded-lg hover:bg-surface-sunken transition-colors duration-150"
                            >
                                <Bookmark className="w-3.5 h-3.5" />
                                Ajouter à une liste
                            </button>
                        )}
                    </AddToListPicker>
                    {isAdmin && (
                        <button
                            onClick={handleEnrichOwners}
                            disabled={enrichingOwners}
                            className="flex items-center gap-2 px-3 py-1.5 bg-surface text-ink text-xs font-semibold rounded-lg hover:bg-surface-sunken disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                        >
                            {enrichingOwners ? (
                                <span className="w-3.5 h-3.5 border-2 border-ink/30 border-t-ink rounded-full motion-safe:animate-spin" />
                            ) : (
                                <UserGroupIcon className="w-3.5 h-3.5" />
                            )}
                            {enrichingOwners
                                ? `Enrichissement... ${enrichProgress ? `${enrichProgress.done}/${enrichProgress.total}` : ""}`
                                : "Enrichir propriétaires"}
                        </button>
                    )}
                </div>
            </div>
        )}
        <div className="bg-surface rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-surface-sunken border-b border-border">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left w-12">
                                <input
                                    type="checkbox"
                                    className="rounded-sm border-border text-accent focus:ring-accent h-4 w-4"
                                    checked={establishments.length > 0 && selectedSignals.size === establishments.length}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Contact</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Coordonées</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Liens</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Entreprise</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                {onDateSortClick ? (
                                    <button onClick={onDateSortClick} className="flex items-center gap-1 hover:text-ink transition-colors duration-150 group">
                                        {dateLabel}
                                        <span className={`text-xs ${dateSortOrder ? 'text-ink-muted' : 'text-ink-faint group-hover:text-ink-muted'}`}>
                                            {dateSortOrder === 'desc' ? '↓' : dateSortOrder === 'asc' ? '↑' : '↕'}
                                        </span>
                                    </button>
                                ) : dateLabel}
                            </th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-ink-faint w-24">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border">
                        {establishments.map((est, index) => {
                            const isLocked = isTrialUser && index >= visibleCount;
                            const isSelected = selectedSignals.has(est._id);
                            const primaryPerson = pickPerson(est);
                            const enrichments: any[] = (primaryPerson as any)?.enrichments || [];
                            const personName: string | undefined = (primaryPerson as any)?.name;
                            const phoneEnrichment = getEnrichment(enrichments, "phone");
                            const emailCompanyEnrichment = getEnrichment(enrichments, "email_company");
                            const emailPersonalEnrichment = getEnrichment(enrichments, "email");
                            const linkedinEnrichment = getEnrichment(enrichments, "linkedin");
                            const birthdateEnrichment = getEnrichment(enrichments, "birthdate");
                            const founderAge = birthdateEnrichment?.data
                                ? computeAgeFromBirthdate(birthdateEnrichment.data)
                                : null;
                            const phone: string | undefined = est.phone || phoneEnrichment?.data?.phone;
                            const businessEmail: string | undefined = est.email || emailCompanyEnrichment?.data?.email;
                            const ownerEmail: string | undefined = emailPersonalEnrichment?.data?.email;
                            const linkedin: string | undefined = linkedinEnrichment?.data?.url;
                            const googleRating = est.ratings?.source_summary?.[0];
                            const _allSignals: any[] = (est as any).signals || [];
                            const badgeSignalTypes: string[] = [...new Set(
                                _allSignals.map((sig: any) => sig.type).filter(Boolean) as string[]
                            )].filter(t => !HIDDEN_SIGNAL_BADGES.has(t));
                            const relevantSignal: any = (() => {
                                if (activeSignalTypes && activeSignalTypes.length > 0) {
                                    const relevant = _allSignals.filter(s => activeSignalTypes.includes(s.type));
                                    if (relevant.length > 0) {
                                        return relevant.reduce((latest: any, s: any) =>
                                            !latest || new Date(s.date) > new Date(latest.date) ? s : latest
                                        );
                                    }
                                }
                                return _allSignals[0];
                            })();
                            const signalDate: string | undefined = relevantSignal?.date;
                            // A closed establishment: the date column should say when it shut, not
                            // when it opened. Latest closing signal wins if several were recorded.
                            const closingSignal: any = _allSignals
                                .filter((s: any) => s.type === "restaurant_closing" && s.date)
                                .reduce((latest: any, s: any) =>
                                    !latest || new Date(s.date) > new Date(latest.date) ? s : latest
                                , null);
                            // Gated on is_active alone: a closing signal is not proof of closure.
                            // Plenty are stale or epoch-dated, and trusting one would strike through
                            // the date of a restaurant that is trading normally.
                            const closedOn: string | undefined =
                                est.is_active === false ? closingSignal?.date : undefined;
                            const daysUntilOpening: number | null = (() => {
                                if (relevantSignal?.type !== "restaurant_opening" || !signalDate) return null;
                                const target = new Date(signalDate); target.setHours(0, 0, 0, 0);
                                const today = new Date(); today.setHours(0, 0, 0, 0);
                                const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
                                return diff >= 0 ? diff : null;
                            })();
                            const upcomingAnniversary = showAnniversary
                                ? getUpcomingAnniversary(est.operations?.opening_date)
                                : null;

                            return (
                                <React.Fragment key={est._id}>
                                {isTrialUser && index === visibleCount && (
                                    <tr key="lock-banner">
                                        <td colSpan={7} className="px-0 py-0">
                                            <div className="flex items-center justify-between px-6 py-3 bg-surface-sunken border-y border-border">
                                                <div className="flex items-center gap-2 text-sm text-ink-muted">
                                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                    </svg>
                                                    <span><strong>{establishments.length - visibleCount} résultats supplémentaires</strong> masqués - disponibles avec l'offre complète</span>
                                                </div>
                                                <a
                                                    href="https://buy.stripe.com/3cIeVcfGAcLu1CFeTqgrS0e"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs font-medium bg-ink text-ink-inverse px-3 py-1.5 rounded-full hover:bg-ink/90 transition-colors duration-150 flex-shrink-0"
                                                >
                                                    Passer à Pro →
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                <tr
                                    className={`transition-colors duration-150 group hover:bg-surface-sunken/60 ${isSelected ? "bg-accent-subtle/40" : ""} ${isLocked ? "blur-sm pointer-events-none select-none" : ""}`}
                                >
                                    {/* Checkbox */}
                                    <td className="px-6 py-4 whitespace-nowrap align-middle" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="rounded-sm border-border text-accent focus:ring-accent h-4 w-4"
                                            checked={isSelected}
                                            onChange={() => toggleSelectSignal(est._id)}
                                        />
                                    </td>

                                    {/* Contact */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex flex-col">
                                            {personName ? (
                                                <>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-sm font-medium text-ink">{formatName(personName)}</span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/signals/founder?founderName=${encodeURIComponent(personName)}`);
                                                        }}
                                                        className="text-xs text-ink-muted mt-0.5 italic text-left hover:text-ink transition-colors duration-150"
                                                    >
                                                        {/* Joined rather than concatenated: the count is null while it loads,
                                                            and a hardcoded separator would leave a dangling "46 ans - ". */}
                                                        {[
                                                            founderAge !== null ? `${founderAge} ans` : null,
                                                            founderCountLabel(personNameCounts[(primaryPerson as any)?._id], countClosed ? "closed" : "active"),
                                                        ].filter(Boolean).join(" - ")}
                                                    </button>
                                                    {/* Signal Types Display */}
                                                    {badgeSignalTypes.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                                            {badgeSignalTypes.map((type: string) => (
                                                                <span
                                                                    key={type}
                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-surface-sunken rounded-sm text-[10px] font-medium text-ink-muted"
                                                                >
                                                                    <span>{SIGNAL_TYPE_ICON[type] || '📡'}</span>
                                                                    <span>{SIGNAL_TYPE_LABEL[type] || type}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-sm text-ink-faint italic">Enrichissement en cours...</span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Coordinates */}
                                    <td className="px-6 py-4 align-middle whitespace-nowrap">
                                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                                            {/* Phone */}
                                            {(() => {
                                                const isMobile = phone ? isMobilePhone(phone) : false;
                                                return (
                                                    <>
                                                    {/* Landline icon — active for non-06/07 */}
                                                        <div className="relative">
                                                            <button
                                                                className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                                                    phone && !isMobile ? "text-ink-muted hover:text-ink hover:bg-surface-sunken" : "opacity-20 cursor-default"
                                                                }`}
                                                                disabled={!phone || isMobile}
                                                                onClick={(e) => { e.stopPropagation(); if (!isMobile) setHoveredPhone(hoveredPhone === est._id ? null : est._id); }}
                                                            >
                                                                <PhoneIcon className="w-5 h-5" />
                                                            </button>
                                                            {phone && !isMobile && (
                                                                <>
                                                                    <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                                                    {hoveredPhone === est._id && (
                                                                        <div ref={phonePopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                                            <div className="bg-surface border-2 border-border rounded-lg">
                                                                                {/* Calling Status Banner */}
                                                                                <div 
                                                                                    className="px-3 py-2 border-b-2 border-border"
                                                                                    style={{ 
                                                                                        backgroundColor: '#ffffff',
                                                                                        borderLeftColor: callingStatus.currentColor,
                                                                                        borderLeftWidth: '3px',
                                                                                        borderLeftStyle: 'solid'
                                                                                    }}
                                                                                >
                                                                                    <div className="flex items-center gap-2">
                                                                                        <div className="flex-1">
                                                                                            <p className="text-xs font-bold" style={{ color: callingStatus.currentColor }}>
                                                                                                {callingStatus.message}
                                                                                            </p>
                                                                                            {callingStatus.nextWindow && (
                                                                                                <p className="text-[11px] text-ink-muted mt-0.5">
                                                                                                    {callingStatus.nextWindow.timeRange} · {callingStatus.nextWindow.startsIn}
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/* Phone Number */}
                                                                                <div className="px-3 py-2">
                                                                                    <p className="text-[10px] font-medium text-ink-muted mb-1.5">Téléphone fixe</p>
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <div className="text-sm font-bold text-ink">{formatPhoneNumber(phone)}</div>
                                                                                        <button 
                                                                                            onClick={(e) => handleCopyPhone(e, phone)} 
                                                                                            className="px-2 py-1 text-[10px] font-semibold bg-ink text-ink-inverse hover:bg-ink/90 rounded-lg transition-colors duration-150"
                                                                                        >
                                                                                            Copier
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        {/* Smartphone icon — active for 06/07 */}
                                                        <div className="relative group">
                                                            <button
                                                                className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                                                    phone && isMobile ? "text-ink-muted hover:text-ink hover:bg-surface-sunken" : "opacity-20 cursor-default"
                                                                }`}
                                                                disabled={!phone || !isMobile}
                                                                onClick={(e) => { e.stopPropagation(); if (isMobile) setHoveredPhone(hoveredPhone === est._id ? null : est._id); }}
                                                            >
                                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                            </button>
                                                            {phone && isMobile && (
                                                                <>
                                                                    <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                                                    {hoveredPhone === est._id && (
                                                                        <div ref={phonePopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                                            <div className="bg-surface border-2 border-border rounded-lg">
                                                                                {/* Calling Status Banner */}
                                                                                <div 
                                                                                    className="px-3 py-2 border-b-2 border-border"
                                                                                    style={{ 
                                                                                        backgroundColor: '#ffffff',
                                                                                        borderLeftColor: callingStatus.currentColor,
                                                                                        borderLeftWidth: '3px',
                                                                                        borderLeftStyle: 'solid'
                                                                                    }}
                                                                                >
                                                                                    <div className="flex items-center gap-2">
                                                                                        <div className="flex-1">
                                                                                            <p className="text-xs font-bold" style={{ color: callingStatus.currentColor }}>
                                                                                                {callingStatus.message}
                                                                                            </p>
                                                                                            {callingStatus.nextWindow && (
                                                                                                <p className="text-[11px] text-ink-muted mt-0.5">
                                                                                                    {callingStatus.nextWindow.timeRange} · {callingStatus.nextWindow.startsIn}
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/* Phone Number */}
                                                                                <div className="px-3 py-2.5">
                                                                                    <div className="flex items-start gap-2 mb-2">
                                                                                        <div className="flex-shrink-0 w-3.5 h-3.5 bg-success flex items-center justify-center rounded-sm">
                                                                                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                                            </svg>
                                                                                        </div>
                                                                                        <div className="flex-1">
                                                                                            <p className="text-[10px] font-bold text-ink">Mobile · Taux optimal</p>
                                                                                            <p className="text-[10px] text-ink-muted">+40% de RDV vs fixe</p>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <div className="text-sm font-bold text-ink">{formatPhoneNumber(phone)}</div>
                                                                                        <button 
                                                                                            onClick={(e) => handleCopyPhone(e, phone)} 
                                                                                            className="px-2 py-1 text-[10px] font-semibold bg-ink text-ink-inverse hover:bg-ink/90 rounded-lg transition-colors duration-150"
                                                                                        >
                                                                                            Copier
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                            {(() => {
                                                const showBusiness = !emailTypeFilter?.length || emailTypeFilter.includes('business');
                                                const showOwner = !emailTypeFilter?.length || emailTypeFilter.includes('owner');
                                                const visibleBusiness = showBusiness ? businessEmail : undefined;
                                                const visibleOwner = showOwner ? ownerEmail : undefined;
                                                const emailCount = (visibleBusiness ? 1 : 0) + (visibleOwner ? 1 : 0);
                                                const hasAnyEmail = emailCount > 0;
                                                return (
                                                    <div className="relative">
                                                        <button
                                                            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                                                hasAnyEmail ? "text-ink-muted hover:text-ink hover:bg-surface-sunken" : "opacity-20 cursor-default"
                                                            }`}
                                                            disabled={!hasAnyEmail}
                                                            onClick={(e) => { e.stopPropagation(); setHoveredEmail(hoveredEmail === est._id ? null : est._id); }}
                                                        >
                                                            <EnvelopeIcon className="w-5 h-5" />
                                                        </button>
                                                        {hasAnyEmail && (
                                                            <>
                                                                <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">{emailCount}</span>
                                                                {hoveredEmail === est._id && (
                                                                    <div ref={emailPopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                                                        <div className="bg-surface border-2 border-border rounded-lg p-3 space-y-2.5">
                                                                            {visibleBusiness && (
                                                                                <div>
                                                                                    <p className="text-[10px] font-medium text-ink-muted mb-1">Email entreprise</p>
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <div className="text-xs font-semibold text-ink truncate">{visibleBusiness}</div>
                                                                                        <button
                                                                                            onClick={(e) => handleCopyEmail(e, visibleBusiness)}
                                                                                            className="px-2 py-1 text-[10px] font-semibold bg-ink text-ink-inverse hover:bg-ink/90 rounded-lg transition-colors duration-150 flex-shrink-0"
                                                                                        >
                                                                                            Copier
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {visibleOwner && (
                                                                                <div>
                                                                                    {visibleBusiness && <div className="border-t border-border pt-2" />}
                                                                                    <p className="text-[10px] font-medium text-ink-muted mb-1">Email propriétaire</p>
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <div className="text-xs font-semibold text-ink truncate">{visibleOwner}</div>
                                                                                        <button
                                                                                            onClick={(e) => handleCopyEmail(e, visibleOwner)}
                                                                                            className="px-2 py-1 text-[10px] font-semibold bg-ink text-ink-inverse hover:bg-ink/90 rounded-lg transition-colors duration-150 flex-shrink-0"
                                                                                        >
                                                                                            Copier
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </td>

                                    {/* Links */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            {/* LinkedIn */}
                                            <div className="relative flex items-center">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setHoveredLinkedin(hoveredLinkedin === est._id ? null : est._id); }}
                                                    className={`transition-colors duration-150 ${linkedin ? "text-[#0A66C2] hover:text-[#004182]" : "text-ink-faint hover:text-ink-muted"}`}
                                                >
                                                    <FaLinkedinIn className="w-5 h-5" />
                                                </button>
                                                {hoveredLinkedin === est._id && (
                                                    <div ref={linkedinPopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-60">
                                                        <div className="bg-surface border-2 border-border rounded-lg p-3">
                                                            {linkedin ? (
                                                                <>
                                                                    <p className="text-[10px] font-medium text-ink-muted mb-2">Profil LinkedIn trouvé</p>
                                                                    <button 
                                                                        onClick={(e) => handleOpenLinkedin(e, linkedin)} 
                                                                        className="w-full bg-[#0077b5] text-white px-3 py-2 rounded-lg hover:bg-[#006399] transition-colors duration-150 text-xs font-semibold"
                                                                    >
                                                                        Ouvrir le profil
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className="text-[10px] font-medium text-ink-muted mb-2">Aucun profil trouvé</p>
                                                                    <button 
                                                                        onClick={(e) => handleManualSearch(e, personName || displayEstablishmentName(est.name))}
                                                                        className="w-full bg-ink text-ink-inverse px-3 py-2 rounded-lg hover:bg-ink/90 transition-colors duration-150 text-xs font-semibold"
                                                                    >
                                                                        Rechercher manuellement
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Website */}
                                            {est.website_url && (
                                                <a
                                                    href={est.website_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className={`transition-colors duration-150 ${detectSocialMedia(est.website_url || "")?.type === "website" ? "text-ink-muted hover:text-ink" : "hover:opacity-80"}`}
                                                    title={`Visit ${est.website_url}`}
                                                    style={detectSocialMedia(est.website_url || "")?.type !== "website" ? { color: detectSocialMedia(est.website_url || "")?.color } : undefined}
                                                >
                                                    {getSocialMediaIcon(detectSocialMedia(est.website_url || "")?.type || "website")}
                                                </a>
                                            )}

                                            {/* Social Networks from establishment */}
                                            {est.social_networks?.map((network: any, idx: number) => {
                                                if (!network.url) return null;
                                                const socialType = detectSocialMedia(network.url);
                                                return (
                                                    <a
                                                        key={idx}
                                                        href={network.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="transition-colors duration-150 hover:opacity-80"
                                                        title={`Visit ${network.platform}`}
                                                        style={socialType ? { color: socialType.color } : undefined}
                                                    >
                                                        {getSocialMediaIcon(socialType?.type || network.platform.toLowerCase())}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </td>

                                    {/* Company */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                <EstablishmentLogo name={est.name || ""} franchise={est.franchise} nafCode={est.registration?.naf_code} />
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-1.5">
                                                    <a
                                                        href={`/establishments/${est._id}`}
                                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/establishments/${est._id}`); }}
                                                        className="text-sm font-semibold text-ink hover:underline text-left"
                                                    >
                                                        {displayEstablishmentName(est.name)}
                                                    </a>
                                                    <ReportSignalBadge counts={reportCounts[est._id]} />
                                                </div>
                                                {est.address?.city && (
                                                    <span className="text-xs text-ink-muted">
                                                        {formatCity(est.address.city)}
                                                        {est.address?.postcode && <span className="text-ink-faint ml-1">({est.address.postcode})</span>}
                                                    </span>
                                                )}
                                                {googleRating && (
                                                    <a
                                                        href={googleRating.url || undefined}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`flex items-center gap-1 text-xs text-ink-muted hover:text-ink transition-colors duration-150 hover:underline${!googleRating.url ? " pointer-events-none" : ""}`}
                                                    >
                                                        <StarIcon className="w-3.5 h-3.5 text-warning fill-current" />
                                                        <span className="font-medium text-ink-muted">{googleRating.rating_avg?.toFixed(1) || "—"}</span>
                                                        <span className="text-ink-muted">({googleRating.rating_count || 0} avis)</span>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Signal date / anniversary / hygiene details */}
                                    <td className="px-6 py-4 align-middle">
                                        {upcomingAnniversary ? (
                                            <div className="flex items-center gap-2 whitespace-nowrap">
                                                <span className="text-sm text-ink-muted">
                                                    {getFormattedDate(est.operations?.opening_date) ?? '-'}
                                                </span>
                                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-canvas text-ink border border-border rounded-sm">
                                                    🎂 {upcomingAnniversary.years} ans {formatAnniversaryDelay(upcomingAnniversary.daysUntil)}
                                                </span>
                                            </div>
                                        ) : activeSignalTypes?.includes("hygiene_control") ? (() => {
                                            const hygieneSignals = (est.signals || []).filter((s: any) => s.type === "hygiene_control");
                                            const sig = hygieneSignals.reduce((latest: any, s: any) =>
                                                !latest || new Date(s.date) > new Date(latest.date) ? s : latest
                                            , null);
                                            const meta = sig?.metadata;
                                            const scoreColors: Record<string, string> = {
                                                "Très satisfaisant": "text-success bg-success-subtle border-success/20",
                                                "Satisfaisant": "text-ink-muted bg-surface-sunken border-border",
                                                "A améliorer": "text-warning bg-warning-subtle border-warning/20",
                                                "A corriger de manière urgente": "text-danger bg-danger-subtle border-danger/20",
                                            };
                                            const scoreClass = meta?.score_label ? (scoreColors[meta.score_label] ?? "text-ink-muted bg-surface-sunken border-border") : "";
                                            return (
                                                <div className="flex flex-col gap-1 min-w-[180px]">
                                                    <span className="text-xs text-ink-faint">{formatDate(sig?.date)}</span>
                                                    {meta?.score_label && (
                                                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm border ${scoreClass}`}>
                                                            {meta.score_label}
                                                        </span>
                                                    )}
                                                    {meta?.evaluation && meta.evaluation !== meta.score_label && (
                                                        <span className="text-xs text-ink-muted">{meta.evaluation}</span>
                                                    )}
                                                    {meta?.follow_up_type && (
                                                        <span className="text-xs text-ink-muted italic">{meta.follow_up_type}</span>
                                                    )}
                                                    {meta?.inspection_number && (
                                                        <span className="text-[11px] text-ink-faint">N° {meta.inspection_number}</span>
                                                    )}
                                                </div>
                                            );
                                        })() : closedOn ? (
                                            <div
                                                className="flex items-center gap-1.5 whitespace-nowrap"
                                                title={`Fermé le ${getFormattedDate(closedOn)}`}
                                            >
                                                <span className="text-sm text-danger line-through">
                                                    {getFormattedDate(closedOn)}
                                                </span>
                                                <span className="text-xs" aria-label="Fermé">🔒</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 whitespace-nowrap">
                                                <span className="text-sm text-ink-muted">
                                                    {getFormattedDate(signalDate) ?? '-'}
                                                </span>
                                                {daysUntilOpening !== null && (
                                                    <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-canvas text-ink border border-border rounded-sm">
                                                        {daysUntilOpening === 0 ? "aujourd'hui" : `dans ${daysUntilOpening} jour${daysUntilOpening > 1 ? 's' : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>

                                    {/* Actions: Add to CRM + Report */}
                                    <td className="px-6 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-center gap-3 relative">

                                            {/* Add to LightCRM button */}
                                            <div className="relative flex items-center">
                                                {lightCrmSuccess[est._id] ? (
                                                    <button className="text-success" title="Ajouté au LightCRM">
                                                        <CheckCircleIcon className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (crmPopupOpen === est._id) {
                                                                setCrmPopupOpen(null);
                                                            } else {
                                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                                const POPUP_WIDTH = 224; // w-56
                                                                const left = rect.left + POPUP_WIDTH > window.innerWidth
                                                                    ? Math.max(8, rect.right - POPUP_WIDTH)
                                                                    : rect.left;
                                                                setCrmPopupPos({ top: rect.bottom + 6, left });
                                                                setCrmPopupOpen(est._id);
                                                            }
                                                        }}
                                                        className="text-ink-faint hover:text-ink-muted transition-colors duration-150"
                                                        title="Ajouter au LightCRM"
                                                    >
                                                        <PlusCircleIcon className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Add to prospection list */}
                                            <AddToListPicker establishmentIds={[est._id]}>
                                                {({ onClick }) => (
                                                    <button
                                                        onClick={onClick}
                                                        className="text-ink-faint hover:text-ink-muted transition-colors duration-150"
                                                        title="Ajouter à une liste"
                                                    >
                                                        <Bookmark className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </AddToListPicker>

                                            {/* Report flag */}
                                            {(() => {
                                                const myReport = myReports[est._id];
                                                const isLoading = reportLoading === est._id;
                                                const isOpen = reportOpen === est._id;
                                                return (
                                                    <div className="relative flex items-center">
                                                        <button
                                                            disabled={isLoading}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setReportOpen(isOpen ? null : est._id);
                                                                setReportComment("");
                                                            }}
                                                            className={`flex flex-col items-center gap-0.5 transition-colors ${
                                                                myReport
                                                                    ? REPORT_COLORS[myReport]
                                                                    : "text-gray-300 hover:text-gray-500"
                                                            }`}
                                                            title={myReport ? REPORT_LABELS[myReport] : "Signaler"}
                                                        >
                                                            {myReport
                                                                ? <FlagIconSolid className="w-4 h-4" />
                                                                : <FlagIcon className="w-4 h-4" />
                                                            }
                                                            {myReport && (
                                                                <span className="text-[10px] leading-none font-medium max-w-[60px] text-center">
                                                                    {REPORT_LABELS[myReport]}
                                                                </span>
                                                            )}
                                                        </button>

                                                        {isOpen && (
                                                            <div
                                                                ref={reportPopupRef}
                                                                className="absolute right-0 top-full mt-1 z-50 w-56 bg-surface border border-border rounded-2xl shadow-float"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                                                                    <span className="text-xs font-semibold text-ink">Signaler ce prospect</span>
                                                                    <button onClick={() => setReportOpen(null)} className="text-gray-400 hover:text-gray-600">
                                                                        <XMarkIcon className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                                {/* Two sections: what is wrong with the data, then how the
                                                                    prospect pays — different questions, and the payment ones
                                                                    are what the row's warning badge is mostly about. */}
                                                                {([
                                                                    { title: "Qualité du signal", types: (Object.keys(REPORT_LABELS) as EstablishmentReportType[]).filter(t => !PAYMENT_REPORT_TYPES.includes(t)) },
                                                                    { title: "Paiement", types: PAYMENT_REPORT_TYPES },
                                                                ]).map(section => (
                                                                    <div key={section.title} className="py-1 border-b border-border last:border-b-0">
                                                                        <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                                                                            {section.title}
                                                                        </p>
                                                                        {section.types.map(type => (
                                                                            <button
                                                                                key={type}
                                                                                onClick={() => handleReport(est._id, type)}
                                                                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                                                                                    myReport === type ? "font-semibold " + REPORT_COLORS[type] : "text-gray-700"
                                                                                }`}
                                                                            >
                                                                                {myReport === type && <FlagIconSolid className="w-3 h-3 flex-shrink-0" />}
                                                                                {REPORT_LABELS[type]}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                                <div className="px-3 pb-2 pt-1 border-t border-border">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Note optionnelle…"
                                                                        value={reportComment}
                                                                        onChange={(e) => setReportComment(e.target.value)}
                                                                        className="w-full text-xs border border-border rounded-lg px-2 py-1 focus:outline-none focus:border-accent"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                                {myReport && (
                                                                    <div className="px-3 pb-2">
                                                                        <button
                                                                            onClick={() => handleRemoveReport(est._id)}
                                                                            className="w-full text-xs text-danger hover:text-danger/80 text-left"
                                                                        >
                                                                            Retirer le signalement
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>

        {/* CRM popup — rendered via portal to escape overflow:hidden */}
        {crmPopupOpen && crmPopupPos && ReactDOM.createPortal(
            <div
                ref={crmPopupRef}
                style={{ position: "fixed", top: crmPopupPos.top, left: crmPopupPos.left, zIndex: 9999 }}
                className="w-56 bg-surface border border-border rounded-2xl shadow-float"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-3 py-2 border-b border-border">
                    <span className="text-xs font-semibold text-ink">Ajouter au CRM</span>
                </div>
                <div className="px-3 py-2.5">
                    {lightCrmSuccess[crmPopupOpen] ? (
                        <div className="flex items-center gap-1.5 text-success text-xs font-medium">
                            <CheckCircleIcon className="w-4 h-4" />
                            Ajouté au LightCRM
                        </div>
                    ) : (
                        <button
                            disabled={crmLoading === `${crmPopupOpen}-lightcrm`}
                            onClick={() => {
                                const est = rawEstablishments.find((e: any) => e._id === crmPopupOpen);
                                if (est) handleAddToLightCrm(est);
                            }}
                            className="w-full bg-ink text-ink-inverse text-xs font-semibold py-1.5 rounded-lg hover:bg-ink/90 transition-colors duration-150 disabled:opacity-40"
                        >
                            {crmLoading === `${crmPopupOpen}-lightcrm` ? "Ajout…" : "Ajouter au LightCRM"}
                        </button>
                    )}
                </div>
            </div>,
            document.body
        )}

        {/* Owner enrichment results — rendered via portal so it sits above everything */}
        {ownerEnrichResults && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] bg-ink/40 flex items-center justify-center p-4" onClick={() => setOwnerEnrichResults(null)}>
                <div
                    className="bg-surface rounded-2xl shadow-float w-full max-w-2xl max-h-[80vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-5 py-3 border-b border-border flex items-center justify-between shrink-0">
                        <h3 className="text-sm font-semibold text-ink">Résultat de l'enrichissement propriétaires</h3>
                        <button onClick={() => setOwnerEnrichResults(null)} className="text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="overflow-y-auto divide-y divide-border">
                        {ownerEnrichResults.map(item => (
                            <div key={item.id} className="px-5 py-3">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-ink">{item.name}</span>
                                    {item.error ? (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-danger-subtle text-danger border border-danger/20">Erreur</span>
                                    ) : item.result?.status === "ok" ? (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-success-subtle text-success border border-success/20">Fondateur(s) trouvé(s)</span>
                                    ) : item.result?.status === "no_data" ? (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-warning-subtle text-warning border border-warning/20">Aucune donnée</span>
                                    ) : (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-danger-subtle text-danger border border-danger/20">Échec</span>
                                    )}
                                </div>
                                {item.error ? (
                                    <p className="text-xs text-danger">{item.error}</p>
                                ) : item.result ? (
                                    <div className="text-xs text-ink-muted space-y-0.5">
                                        <p>
                                            {item.result.founders.length} fondateur{item.result.founders.length > 1 ? "s" : ""} trouvé{item.result.founders.length > 1 ? "s" : ""}
                                            {item.result.linkedPersonIds.length > 0 && ` · ${item.result.linkedPersonIds.length} personne(s) liée(s)`}
                                            {item.result.signalCreated && " · changement d'exploitant signalé"}
                                            {item.result.multiSiteTagged && " · multi-site détecté"}
                                        </p>
                                        <p className="text-gray-400">
                                            Sources : {item.result.sources.map(s => `${s.name.toUpperCase()} ${s.ok ? `(${s.count ?? 0})` : "✗"}`).join(" · ")}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
}
